import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { LinkOutlined as AssignIcon, LocalShippingOutlined as TruckIcon } from '@mui/icons-material';
import type { TruckSchema } from '../../types/transportation.type';
import type { DriverViewModel } from './DriverViewDialog';

const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
} as const;

function FormSectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        fontWeight: 600,
        display: 'block',
        mb: 1.5,
      }}
    >
      {children}
    </Typography>
  );
}

function getDriverName(driver: DriverViewModel): string {
  const data = driver.data as { first_name?: string; last_name?: string; phone?: string } | undefined;
  const first = data?.first_name?.trim();
  const last = data?.last_name?.trim();
  const fullName = [first, last].filter(Boolean).join(' ');
  if (fullName) return fullName;
  return driver.username || driver.phone || data?.phone || '—';
}

function getDriverSubtitle(driver: DriverViewModel): string | null {
  const data = driver.data as { first_name?: string; last_name?: string } | undefined;
  const first = data?.first_name?.trim();
  const last = data?.last_name?.trim();
  const fullName = [first, last].filter(Boolean).join(' ');
  const email = driver.username?.trim();
  if (fullName && email) return email;
  return null;
}

function getTruckPlate(truck: TruckSchema): string | null {
  const plate = truck.data?.en_plate_number?.trim() || truck.data?.ar_plate_number?.trim();
  return plate || null;
}

export interface AssignTruckDialogProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  /** Whether the current user is permitted to assign this driver to a truck. */
  canSubmit: boolean;
  driver: DriverViewModel | null;
  availableTrucks: TruckSchema[];
  truckId: string;
  onTruckIdChange: (truckId: string) => void;
  onSubmit: () => void;
}

const AssignTruckDialog: React.FC<AssignTruckDialogProps> = ({
  open,
  onClose,
  loading,
  canSubmit,
  driver,
  availableTrucks,
  truckId,
  onTruckIdChange,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const driverName = driver ? getDriverName(driver) : '—';
  const driverSubtitle = driver ? getDriverSubtitle(driver) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 2.5,
          py: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              flexShrink: 0,
            }}
          >
            <TruckIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, m: 0 }}>
              {t('transportation.drivers.assignToTruck')}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              title={driverSubtitle ? `${driverName} · ${driverSubtitle}` : driverName}
              sx={{ display: 'block', mt: 0.25 }}
            >
              {driverName}
            </Typography>
            {driverSubtitle ? (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                title={driverSubtitle}
                sx={{ display: 'block' }}
              >
                {driverSubtitle}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </DialogTitle>

      <Box component="form" onSubmit={(e) => { e.preventDefault(); onSubmit(); }} noValidate>
        <DialogContent sx={{ px: 2.5, py: 2 }}>
          {driver?.assignedTruck ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('transportation.drivers.currentlyAssignedNotice', {
                truckName: driver.assignedTruck.name,
              })}
            </Alert>
          ) : null}
          <Box>
            <FormSectionTitle>{t('transportation.drivers.selectTruck')}</FormSectionTitle>
            <TextField
              fullWidth
              size="small"
              sx={fieldSx}
              select
              label={t('transportation.drivers.selectTruck')}
              value={truckId}
              onChange={(e) => onTruckIdChange(e.target.value)}
              disabled={availableTrucks.length === 0}
              helperText={
                availableTrucks.length === 0
                  ? t('transportation.drivers.noAvailableTrucks')
                  : undefined
              }
            >
              {availableTrucks.length === 0 ? (
                <MenuItem disabled value="">
                  {t('transportation.drivers.noAvailableTrucks')}
                </MenuItem>
              ) : (
                availableTrucks.map((truck) => {
                  const plate = getTruckPlate(truck);
                  const secondary = [plate, `${truck.capacity} kg`].filter(Boolean).join(' · ');
                  return (
                    <MenuItem key={truck.id} value={truck.id}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                          {truck.name}
                        </Typography>
                        {secondary ? (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {secondary}
                          </Typography>
                        ) : null}
                      </Box>
                    </MenuItem>
                  );
                })
              )}
            </TextField>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 2,
            gap: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!truckId || loading || availableTrucks.length === 0 || !canSubmit}
            startIcon={<AssignIcon />}
            sx={{ textTransform: 'none' }}
          >
            {loading ? t('transportation.drivers.assigning') : t('transportation.drivers.assign')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default AssignTruckDialog;
